/**
 * Hook for image CRUD operations
 * 
 * Handles uploads with sliding window concurrency for reliability.
 */

import { useState, useCallback } from 'react';
import type { ListingImage, UploadResult, FailedUpload } from '../types';
import * as api from '../api/client';

interface UseImagesState {
  images: ListingImage[];
  autoHeroId: string | null;
  loading: boolean;
  error: string | null;
  failedUploads: FailedUpload[];
  recentlyUploaded: Set<string>;
  uploadProgress: { current: number; total: number } | null;
}

interface UseImagesReturn extends UseImagesState {
  loadImages: (listingId: string) => Promise<void>;
  uploadFiles: (listingId: string, files: File[]) => Promise<UploadResult[]>;
  reorderImages: (listingId: string, newOrder: string[]) => Promise<void>;
  deleteImage: (imageId: string) => Promise<void>;
  setHero: (listingId: string, imageId: string) => Promise<void>;
  clearFailedUploads: () => void;
  retryFailedUploads: (listingId: string) => Promise<void>;
}

// Concurrency settings - keep low for reliability
const MAX_CONCURRENT_UPLOADS = 3;

/**
 * Hook for managing listing images
 */
export function useImages(): UseImagesReturn {
  const [state, setState] = useState<UseImagesState>({
    images: [],
    autoHeroId: null,
    loading: false,
    error: null,
    failedUploads: [],
    recentlyUploaded: new Set(),
    uploadProgress: null,
  });

  // Store failed files for retry
  const [failedFiles, setFailedFiles] = useState<Map<string, File>>(new Map());

  /**
   * Load all images for a listing
   */
  const loadImages = useCallback(async (listingId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    
    try {
      const response = await api.getImages(listingId);
      setState((prev) => ({
        ...prev,
        images: response.images,
        autoHeroId: response.auto_hero_id,
        loading: false,
      }));
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load images',
      }));
    }
  }, []);

  /**
   * Upload a single file - returns result with file reference for tracking
   * 
   * Server assigns position atomically to avoid race conditions.
   */
  const uploadSingleFile = async (
    listingId: string,
    file: File
  ): Promise<{ result: UploadResult; file: File }> => {
    try {
      // Get upload URL - server assigns position atomically
      const { upload_url, image_id } = await api.getUploadUrl(listingId, file.name);
      
      // Upload to Cloudflare
      await api.uploadToCloudflare(upload_url, file);
      
      // Confirm upload
      const image = await api.confirmUpload(image_id);
      
      return {
        result: {
          filename: file.name,
          success: true,
          imageId: image_id,
          image,
        },
        file,
      };
    } catch (err) {
      return {
        result: {
          filename: file.name,
          success: false,
          error: err instanceof Error ? err.message : 'Upload failed',
        },
        file,
      };
    }
  };

  /**
   * Process a completed upload result
   */
  const processResult = (
    result: UploadResult,
    file: File,
    newRecentlyUploaded: Set<string>,
    newFailedUploads: FailedUpload[],
    newFailedFiles: Map<string, File>
  ) => {
    if (result.success && result.imageId) {
      newRecentlyUploaded.add(result.imageId);
      
      // Add successful image to state immediately
      if (result.image) {
        setState(prev => ({
          ...prev,
          images: [...prev.images, result.image!],
        }));
      }
    } else if (!result.success) {
      newFailedUploads.push({
        filename: result.filename,
        error: result.error || 'Unknown error',
      });
      newFailedFiles.set(result.filename, file);
    }
  };

  /**
   * Upload multiple files with sliding window concurrency
   * 
   * Maintains exactly MAX_CONCURRENT_UPLOADS in flight at all times.
   * As soon as one completes, the next one starts.
   * This is more reliable than batch-based because:
   * - Individual failures don't block other uploads
   * - Progress updates smoothly
   * - Maximum parallelism is maintained
   */
  const uploadFiles = useCallback(async (
    listingId: string,
    files: File[]
  ): Promise<UploadResult[]> => {
    const results: UploadResult[] = [];
    const newRecentlyUploaded = new Set<string>();
    const newFailedUploads: FailedUpload[] = [];
    const newFailedFiles = new Map<string, File>();

    // Initialize progress
    setState(prev => ({
      ...prev,
      uploadProgress: { current: 0, total: files.length },
    }));

    let completedCount = 0;
    let fileIndex = 0;
    
    // Track in-flight uploads - use Set with auto-cleanup via finally()
    const inFlight = new Set<Promise<{ result: UploadResult; file: File }>>();

    // Helper to start a new upload with auto-cleanup
    // Server assigns position atomically to avoid race conditions
    const startUpload = (file: File) => {
      const promise = uploadSingleFile(listingId, file);
      inFlight.add(promise);
      // Auto-remove from Set when promise settles (success or failure)
      promise.finally(() => inFlight.delete(promise));
      return promise;
    };

    // Initial fill of the upload slots
    while (fileIndex < files.length && inFlight.size < MAX_CONCURRENT_UPLOADS) {
      startUpload(files[fileIndex]);
      fileIndex++;
    }

    // Process uploads as they complete, starting new ones to maintain concurrency
    while (inFlight.size > 0) {
      // Wait for any one to complete - Promise.race returns the VALUE, not the promise
      // The promise auto-removes from inFlight via finally()
      const { result, file } = await Promise.race(inFlight);
      
      // Track result
      results.push(result);
      completedCount++;

      // Update progress
      setState(prev => ({
        ...prev,
        uploadProgress: { current: completedCount, total: files.length },
      }));

      // Process result (add to state, track failures)
      processResult(result, file, newRecentlyUploaded, newFailedUploads, newFailedFiles);

      // Start next upload if there are more files
      if (fileIndex < files.length) {
        startUpload(files[fileIndex]);
        fileIndex++;
      }
    }

    // Final state update
    setState((prev) => ({
      ...prev,
      failedUploads: [...prev.failedUploads, ...newFailedUploads],
      recentlyUploaded: new Set([...prev.recentlyUploaded, ...newRecentlyUploaded]),
      uploadProgress: null, // Clear progress when done
    }));

    setFailedFiles((prev) => new Map([...prev, ...newFailedFiles]));

    return results;
  }, []);

  /**
   * Reorder images
   */
  const reorderImages = useCallback(async (
    listingId: string,
    newOrder: string[]
  ) => {
    // Optimistically update UI
    setState((prev) => {
      const imageMap = new Map(prev.images.map((img) => [img.id, img]));
      const reordered = newOrder
        .map((id, index) => {
          const img = imageMap.get(id);
          if (img) {
            return { ...img, position: index };
          }
          return null;
        })
        .filter((img): img is ListingImage => img !== null);
      
      return { ...prev, images: reordered };
    });

    try {
      await api.reorderImages(listingId, newOrder);
    } catch (err) {
      // Revert on failure by reloading
      await loadImages(listingId);
      setState((prev) => ({
        ...prev,
        error: 'Failed to reorder images. Please try again.',
      }));
    }
  }, [loadImages]);

  /**
   * Delete an image
   */
  const deleteImage = useCallback(async (imageId: string) => {
    // Save image for potential rollback
    let deletedImage: ListingImage | undefined;
    
    // Optimistically remove from UI
    setState((prev) => {
      deletedImage = prev.images.find((img) => img.id === imageId);
      return {
        ...prev,
        images: prev.images.filter((img) => img.id !== imageId),
      };
    });

    try {
      await api.deleteImage(imageId);
    } catch (err) {
      // Restore the image on failure
      setState((prev) => {
        const restoredImages = deletedImage 
          ? [...prev.images, deletedImage].sort((a, b) => a.position - b.position)
          : prev.images;
        return {
          ...prev,
          images: restoredImages,
          error: "Couldn't delete image. Please try again.",
        };
      });
    }
  }, []);

  /**
   * Toggle hero image for a listing
   */
  const [settingHero, setSettingHero] = useState(false);

  const setHero = useCallback(async (listingId: string, imageId: string) => {
    if (settingHero) return; // Prevent rapid clicks
    setSettingHero(true);
    console.log('[setHero] Toggling hero:', { listingId, imageId });
    try {
      const response = await api.setHero(listingId, imageId);
      console.log('[setHero] Response:', response);
      setState((prev) => ({ ...prev, images: response.images, autoHeroId: response.auto_hero_id, error: null }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to set hero image';
      console.error('[setHero] Error:', msg);
      setState((prev) => ({
        ...prev,
        error: msg,
      }));
    } finally {
      setSettingHero(false);
    }
  }, [settingHero]);

  /**
   * Clear failed uploads
   */
  const clearFailedUploads = useCallback(() => {
    setState((prev) => ({ ...prev, failedUploads: [] }));
    setFailedFiles(new Map());
  }, []);

  /**
   * Retry failed uploads
   */
  const retryFailedUploads = useCallback(async (listingId: string) => {
    const filesToRetry = Array.from(failedFiles.values());
    
    // Clear current failures before retry
    setState((prev) => ({ ...prev, failedUploads: [] }));
    setFailedFiles(new Map());
    
    // Retry uploads
    await uploadFiles(listingId, filesToRetry);
  }, [failedFiles, uploadFiles]);

  return {
    ...state,
    loadImages,
    uploadFiles,
    reorderImages,
    deleteImage,
    setHero,
    clearFailedUploads,
    retryFailedUploads,
  };
}
